import { useState, useEffect } from 'react';
import {
	PencilIcon,
	CheckIcon,
	XMarkIcon,
	ChevronDownIcon,
	ChevronRightIcon,
} from '@heroicons/react/24/outline';
import useAsync from '../../hooks/useAsync';
import useSettings from '../../hooks/useSettings';
import {
	getManufacturers,
	getModels,
	updateManufacturer,
	updateModel,
} from '../../api/assets';
import { getModuleFields } from '../../api/modules';

const ASSET_MODULE_ID = process.env.REACT_APP_MODULE_ID_ASSETS;

// Built-in variables that are always available
const BUILT_IN_VARIABLES = [
	{ key: 'serialNumber', label: 'Serial Number' },
	{ key: 'location', label: 'Location' },
	{ key: 'tag', label: 'Tag' },
	{ key: 'alias', label: 'Alias' },
];

// Field types that are allowed for template variables
const ALLOWED_FIELD_TYPES = ['text', 'textarea', 'numeric'];

const AssetDisplayTemplate = () => {
	const settings = useSettings();
	const [template, setTemplate] = useState('');
	const [isSaving, setIsSaving] = useState(false);
	const [hasChanges, setHasChanges] = useState(false);
	//const moduleFields = [];

	const { value: moduleFields } = useAsync(getModuleFields, {
		defaultPayload: { id: ASSET_MODULE_ID },
	});

	console.log('moduleFields: ', moduleFields);

	/*const { value: moduleFields } = useAsync(
		() => getModuleFields({ id: ASSET_MODULE_ID }),
		{ immediate: true },
	);*/

	// Get custom attributes that are text or number types
	const customAttributes = (moduleFields || [])
		.filter(
			(field) =>
				ALLOWED_FIELD_TYPES.includes(field.type) && field.active !== false,
		)
		.map((field) => ({
			key: field.key,
			label: field.label,
			type: field.type,
		}));

	console.log('customAttributes: ', customAttributes);

	// Load saved template on mount
	useEffect(() => {
		const savedTemplate = settings.get('assetDisplay');
		if (savedTemplate) {
			setTemplate(savedTemplate);
		}
	}, [settings.organization]);

	const handleTemplateChange = (e) => {
		setTemplate(e.target.value);
		setHasChanges(true);
	};

	const handleSave = () => {
		setIsSaving(true);
		try {
			settings.set('company', 'assetDisplay', template);
			setHasChanges(false);
		} catch (error) {
			console.error('Failed to save asset display template:', error);
		} finally {
			setIsSaving(false);
		}
	};

	const insertVariable = (key) => {
		const newTemplate = template + `{{ ${key} }}`;
		setTemplate(newTemplate);
		setHasChanges(true);
	};

	const allVariables = [...BUILT_IN_VARIABLES, ...customAttributes];

	return (
		<div className="mb-8">
			<h3 className="text-sm font-medium text-gray-900 mb-2">
				Asset Display Template
			</h3>
			<p className="text-sm text-gray-500 mb-4">
				Customize how asset information appears throughout the platform. Use{' '}
				<code className="bg-gray-100 px-1 py-0.5 rounded text-xs">
					{'{{ variableName }}'}
				</code>{' '}
				syntax to include dynamic values.
			</p>

			<div className="space-y-4">
				<div>
					<label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">
						Template{' '}
						<span className="text-gray-500">(Default "S/N: ABC123)</span>
					</label>

					<div className="flex flex-wrap gap-2 my-2">
						{allVariables.map((variable) => (
							<button
								key={variable.key}
								type="button"
								onClick={() => insertVariable(variable.key)}
								className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
								title={`Click to insert {{ ${variable.key} }}`}
							>
								{variable.label}
							</button>
						))}
					</div>
					<div className="flex gap-2">
						<input
							type="text"
							value={template}
							onChange={handleTemplateChange}
							placeholder="e.g., S/N: {{ serialNumber }} - Tag: {{ tag }}"
							className="block flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2"
						/>
						<button
							type="button"
							onClick={handleSave}
							disabled={isSaving || !hasChanges}
							className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
						>
							{isSaving ? 'Saving...' : 'Save'}
						</button>
					</div>
				</div>

				{template && (
					<div className="bg-gray-50 rounded-lg p-3">
						<label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">
							Preview
						</label>
						<p className="text-sm text-gray-700">
							{template
								.replace(/\{\{\s*serialNumber\s*\}\}/g, 'ABC123')
								.replace(/\{\{\s*location\s*\}\}/g, 'Building A')
								.replace(/\{\{\s*tag\s*\}\}/g, 'ASSET-001')
								.replace(/\{\{\s*alias\s*\}\}/g, 'Main Server')
								.replace(/\{\{\s*\w+\s*\}\}/g, '[custom value]')}
						</p>
					</div>
				)}
			</div>
		</div>
	);
};

const EditableItem = ({ item, onSave, isSubmitting, isModel = false }) => {
	const [isEditing, setIsEditing] = useState(false);
	const [editValue, setEditValue] = useState(item.name);

	const handleSave = async () => {
		if (editValue.trim() && editValue !== item.name) {
			await onSave(item.id, editValue.trim());
		}
		setIsEditing(false);
	};

	const handleCancel = () => {
		setEditValue(item.name);
		setIsEditing(false);
	};

	const handleKeyDown = (e) => {
		if (e.key === 'Enter') {
			handleSave();
		} else if (e.key === 'Escape') {
			handleCancel();
		}
	};

	return (
		<div
			className={`flex items-center justify-between group py-3 ${isModel ? 'pl-8' : ''}`}
		>
			{isEditing ? (
				<div className="flex items-center gap-2 flex-1">
					<input
						type="text"
						value={editValue}
						onChange={(e) => setEditValue(e.target.value)}
						onKeyDown={handleKeyDown}
						className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2"
						autoFocus
						disabled={isSubmitting}
					/>
					<button
						type="button"
						onClick={handleSave}
						disabled={isSubmitting || !editValue.trim()}
						className="p-1 text-green-600 hover:text-green-800 disabled:opacity-50"
					>
						<CheckIcon className="h-5 w-5" />
					</button>
					<button
						type="button"
						onClick={handleCancel}
						disabled={isSubmitting}
						className="p-1 text-gray-400 hover:text-gray-600"
					>
						<XMarkIcon className="h-5 w-5" />
					</button>
				</div>
			) : (
				<>
					<span
						className={`text-sm ${isModel ? 'text-gray-600' : 'text-gray-900 font-medium'}`}
					>
						{item.name}
					</span>
					<button
						type="button"
						onClick={() => setIsEditing(true)}
						className="p-1 text-gray-400 hover:text-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity"
					>
						<PencilIcon className="h-4 w-4" />
					</button>
				</>
			)}
		</div>
	);
};

const ManufacturerGroup = ({
	manufacturer,
	models,
	onUpdateManufacturer,
	onUpdateModel,
	isSubmitting,
}) => {
	const [isExpanded, setIsExpanded] = useState(true);

	const manufacturerModels = models.filter(
		(model) =>
			model.manufacturer?.id === manufacturer.id ||
			model.manufacturer_id === manufacturer.id,
	);

	return (
		<div className="border-b border-gray-200 last:border-b-0">
			<div className="flex items-center hover:bg-gray-50 px-4">
				<button
					type="button"
					onClick={() => setIsExpanded(!isExpanded)}
					className="p-1 mr-2 text-gray-400 hover:text-gray-600"
				>
					{isExpanded ? (
						<ChevronDownIcon className="h-4 w-4" />
					) : (
						<ChevronRightIcon className="h-4 w-4" />
					)}
				</button>
				<div className="flex-1">
					<EditableItem
						item={manufacturer}
						onSave={onUpdateManufacturer}
						isSubmitting={isSubmitting}
					/>
				</div>
				<span className="text-xs text-gray-400 mr-2">
					{manufacturerModels.length} model
					{manufacturerModels.length !== 1 ? 's' : ''}
				</span>
			</div>
			{isExpanded && manufacturerModels.length > 0 && (
				<div className="bg-gray-50 border-t border-gray-100">
					{manufacturerModels
						.sort((a, b) =>
							(a.name || '')
								.toLowerCase()
								.localeCompare((b.name || '').toLowerCase()),
						)
						.map((model) => (
							<div
								key={model.id}
								className="px-4 border-b border-gray-100 last:border-b-0"
							>
								<EditableItem
									item={model}
									onSave={onUpdateModel}
									isSubmitting={isSubmitting}
									isModel
								/>
							</div>
						))}
				</div>
			)}
		</div>
	);
};

const AssetSettings = () => {
	const [isSubmitting, setIsSubmitting] = useState(false);

	const { value: manufacturers, execute: refreshManufacturers } = useAsync(
		getManufacturers,
		{ immediate: true },
	);

	const { value: models, execute: refreshModels } = useAsync(getModels, {
		immediate: true,
	});

	const { execute: onUpdateManufacturer } = useAsync(updateManufacturer, {
		immediate: false,
	});

	const { execute: onUpdateModel } = useAsync(updateModel, {
		immediate: false,
	});

	const handleUpdateManufacturer = async (id, name) => {
		setIsSubmitting(true);
		try {
			await onUpdateManufacturer({ id, name });
			await refreshManufacturers();
		} catch (error) {
			console.error('Failed to update manufacturer:', error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleUpdateModel = async (id, name) => {
		setIsSubmitting(true);
		try {
			await onUpdateModel({ id, name });
			await refreshModels();
		} catch (error) {
			console.error('Failed to update model:', error);
		} finally {
			setIsSubmitting(false);
		}
	};

	// Sort manufacturers alphabetically
	const sortedManufacturers = [...(manufacturers?.rows || [])].sort((a, b) =>
		(a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()),
	);

	const allModels = models?.rows || [];

	return (
		<div>
			<h2 className="text-base font-semibold leading-6 text-gray-900">
				Asset Settings
			</h2>
			<p className="mt-1 text-sm text-gray-500 mb-6">
				Manage how assets are displayed and configure manufacturers and models.
			</p>

			<AssetDisplayTemplate />

			<div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg mt-6">
				<div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
					<h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider">
						Manufacturers & Models
					</h3>
				</div>
				<div className="bg-white divide-y divide-gray-200">
					{sortedManufacturers.length === 0 ? (
						<div className="px-4 py-4 text-sm text-gray-500">
							No manufacturers found
						</div>
					) : (
						sortedManufacturers.map((manufacturer) => (
							<ManufacturerGroup
								key={manufacturer.id}
								manufacturer={manufacturer}
								models={allModels}
								onUpdateManufacturer={handleUpdateManufacturer}
								onUpdateModel={handleUpdateModel}
								isSubmitting={isSubmitting}
							/>
						))
					)}
				</div>
			</div>
		</div>
	);
};

export default AssetSettings;
